import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { BaseComponent } from '@gci/components/base-page/base-component';
import { ToastService } from '@gci/components/toast/toast.service';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from 'app/core/auth/auth.service';
import { CreateGroupRequest, IGroup } from 'app/models/group.type';
import { GroupService } from 'app/modules/admin/services/group.service';
import { RoleService } from 'app/modules/admin/services/role.service';
import { SharedModule } from 'app/modules/admin/shared.module';
import { take, takeUntil } from 'rxjs';
import { SelectIconComponent } from "../../../../components/select-icon/select-icon.component";
import { SolutionService } from 'app/modules/admin/services/solution.service';
import { Solution } from 'app/models/solution.type';

export type addGroupType = 'create' | 'edit';

@Component({
    selector: 'app-group-add-form',
    standalone: true,
    imports: [
        SharedModule,
        MatInputModule,
        MatSelectModule,
        MatFormFieldModule,
        SelectIconComponent
    ],
    exportAs: 'group-add-form',
    templateUrl: './group-add-form.component.html'
})
export class GroupAddFormComponent extends BaseComponent implements OnInit, OnChanges {

    // -----------------------------------------------------------------------------------------------------
    // @ Properties
    // -----------------------------------------------------------------------------------------------------

    @Input({required: true}) type!: addGroupType;
    @Input() data?: IGroup;
    @Output() changeType: EventEmitter<addGroupType> = new EventEmitter<addGroupType>();
    @Output() cancel: EventEmitter<addGroupType> = new EventEmitter<addGroupType>();
    @Output() submitForm: EventEmitter<any> = new EventEmitter<any>();

    public groupForm!: FormGroup;
    public solutions: Solution[] = [];

    // -----------------------------------------------------------------------------------------------------
    // @ Constructor
    // -----------------------------------------------------------------------------------------------------

    constructor(
        private fb: FormBuilder,
        private _roleService: RoleService,
        private _groupService: GroupService,
        private _authService: AuthService,
        private _toastService: ToastService,
        private _translocoService: TranslocoService,
        private _solutionService: SolutionService,
    ) {
        super("AddGroupComponent", _translocoService)

        this.groupForm = this.fb.group({
            solution: ['', Validators.required],
            // solution_id: [''],
            alias: ['', Validators.required], // The alias is unique
            description: ['', Validators.required], // The description is used to store the icon 
            notes: ['', Validators.required] // The notes is used as group name
        })

        this._solutionService.allItemsNewFormat$
        .pipe(takeUntil(this.unsubscribeAll))
        .subscribe({
            next: response => {
                if(response) {
                    const {data} = response;
                    this.solutions = data;

                    if(!this.groupForm.value.solution) {
                        const defaultSolution = this.solutions[this.solutions.length - 1];
                        this.groupForm.patchValue({
                            solution: defaultSolution.key,
                            // solution_id: defaultSolution.id,
                            description: defaultSolution.icon,
                        })
                    }
                }
            }
        })

        this._solutionService.getSolution({limit: 100}, true).pipe(take(1)).subscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    override ngOnInit(): void {
        super.ngOnInit()
        this._roleService.getRoles()
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('data' in changes && this.data) {

            this.groupForm.patchValue({
                // solution_id: this.data.solution_id,
                solution: this.data?.solution,
                notes: this.data?.notes,
                alias: this.data?.alias,
                description: this.data?.description
            })
            
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public Methods
    // -----------------------------------------------------------------------------------------------------

    getSolutionName(id: string) {
        return this.solutions.find(i => i.key === id)?.title || 'Select Solution';
    }

    choiseAdd(type: addGroupType) {
        this.changeType.emit(type);
    }

    cancelClick() {
        this.cancel.emit();
    }

    createGroupSubmit() {

        let body: CreateGroupRequest = { 
            ...this.groupForm.value, 
            tenant_id: this._authService.currentUser?.tenant_id,
        }

        this._groupService.createGroup(body).subscribe({
            next: response => {
                this.cancelClick();
                this.groupForm.reset();
                this.submitForm.emit(response);

                const toastSuccessText = this._translocoService.translate('createGroup');
                this._toastService.success(toastSuccessText);
            },
            error: err => {
                const toastErrorText = this._translocoService.translate('createGroupError', {error: err});
                this._toastService.error(toastErrorText);
            }
        });
    }

    updateGroupSubmit() {
        let body = this.groupForm.value

        this._groupService.updateGroup(body).subscribe({
            next: response => {
                this.cancelClick();
                this.groupForm.reset();
                this.submitForm.emit(response)

                const toastSuccessText = this._translocoService.translate('updateGroup');
                this._toastService.success(toastSuccessText);
            },
            error: err => {
                const toastErrorText = this._translocoService.translate('updateGroupError', {error: err});
                this._toastService.error(toastErrorText);
                this.cancelClick();
                this.groupForm.reset();
            }
        })
    }

    pasteClipboard() {
        navigator.clipboard.readText().then((text) => {
            this.groupForm.patchValue({
                group_id: text
            })
        })
    }

    selectIcon(icon: string) {
        this.groupForm.patchValue({
            description: icon
        })
    }

    // selectSolutionHandle(solution: Solution) {
    //     this.groupForm.patchValue({
    //         solution_id: solution.id,
    //         solution: solution.key,
    //         description: solution.icon
    //     })
    // }
}
